import { useEffect, useRef } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db, messagingPromise } from "../firebase";
import { useAuth } from "./AuthContext";

const VAPID_KEY = "BBAo_c59TcR1XwFeYPHJUlN9t_NF10VCFEioYPIf9wWbPGliJo02FHTxTth4IiIBRyoRUd-LTG2KdeBYWZlL7kI";

export function useNotifications() {
  const { user, profile } = useAuth();

  // Evita pedir el permiso más de una vez en la misma sesión de la app —
  // sin esto, si Firestore entrega el perfil dos veces (primero de la
  // caché local, luego confirmado del servidor), "profile.config" cambia
  // de referencia entre una y otra, el efecto de abajo se vuelve a
  // disparar, y se pedía el permiso dos veces seguidas.
  const askedRef = useRef(false);

  useEffect(() => {
    if (!user || !profile?.config) return;
    if (askedRef.current) return;
    askedRef.current = true;

    async function init() {
      const messaging = await messagingPromise;
      if (!messaging) return;
      if (typeof Notification === "undefined") return;

      try {
        // Si el usuario (o Android) ya respondió antes —concedido o
        // denegado—, esa respuesta la recuerda el propio sistema para
        // siempre: NO hay que volver a pedirla cada vez que se abre la
        // app. Antes se llamaba a requestPermission() sin comprobar esto,
        // por lo que Android volvía a mostrar el aviso en cada apertura.
        if (Notification.permission === "default") {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") return;
        } else if (Notification.permission !== "granted") {
          return; // el usuario ya lo denegó antes — se respeta esa elección
        }

        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (!token) return;

        // Usar updateDoc en lugar de setDoc para no sobreescribir el documento
        await updateDoc(doc(db, "users", user.uid), {
          fcmTokens: arrayUnion(token)
        });

      } catch (err) {
        console.warn("FCM init error:", err);
      }
    }

    init();
  }, [user, profile?.config]);

  // Escucha de notificaciones con la app abierta — en un efecto aparte,
  // ligado solo al usuario (no al perfil), para que no se registre varias
  // veces cada vez que el efecto de arriba se re-evalúe.
  useEffect(() => {
    if (!user) return;
    let unsubscribe;
    let cancelled = false;

    (async () => {
      const messaging = await messagingPromise;
      if (!messaging || cancelled) return;
      unsubscribe = onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {};
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification(title || "ARIA Wallet", {
            body: body || "",
            icon: "/icon-192.png",
          });
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [user]);
}
